import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { format, parseISO } from 'date-fns';
import type { PaymentRequestPaymentMethodEvent } from '@stripe/stripe-js';

import { Button, Card } from '@/design-system/primitives';
import { PageHeader } from '@/components/app-shell';
import { PaymentMethodPicker } from '@/features/marketplace/components/PaymentMethodPicker';
import { useApplePaySheet, type WalletSheet } from '@/features/marketplace/applePaySheet';
import { track } from '@/lib/analytics';
import { ApiError } from '@/lib/api/client';
import { useMySubscriptions, usePaymentMethods } from '@/lib/api/hooks';
import {
  useMembershipOffer,
  usePurchaseMembership,
  type MembershipOffer,
  type MembershipPurchaseBody,
} from '@/lib/api/membershipHooks';
import { getStripe } from '@/lib/stripe';
import { clearMembershipLinks, membershipInvite, repOfferTags } from './membershipLinks';

function dollars(cents: number): string {
  const value = cents / 100;
  return value % 1 === 0 ? `$${value.toFixed(0)}` : `$${value.toFixed(2)}`;
}

function shortDate(value: string | null | undefined): string | null {
  if (!value) return null;
  try {
    return format(parseISO(value), 'MMM d');
  } catch {
    return null;
  }
}

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

type Welcome = {
  discountPercent: number;
  perkCents: number | null;
  planName: string | null;
  renewalDate: string | null;
  finishing: boolean;
};

/** /app/membership: join the $199/yr System Membership from the portal. */
export function MembershipJoinScreen() {
  const { data: offer, isPending, isError } = useMembershipOffer();
  // Held here, not in the form: joining refreshes the offer to reason
  // 'member', which replaces the form with the already-a-member card.
  const [welcome, setWelcome] = useState<Welcome | null>(null);
  return (
    <div className="flex flex-col">
      <PageHeader
        title="System Membership"
        description="Member pricing on everything, at-home blood tests, and half off your next renewal when you join."
      />
      <div className="flex flex-col px-15 md:px-40 py-25 gap-20 max-w-[65rem] mx-auto w-full">
        {welcome ? (
          <MembershipWelcome welcome={welcome} />
        ) : isPending ? (
          <p className="type-body-sm text-ink-muted">Loading…</p>
        ) : isError || !offer ? (
          <p className="type-body-sm text-ink-secondary">
            Membership couldn’t load. Refresh the page to try again.
          </p>
        ) : offer.reason === 'member' ? (
          <Card padding="lg">
            <p className="type-body-md">You’re already a System Member.</p>
            <Link to="/app/account" className="type-body-sm text-accent hover:underline">
              See your membership in Account →
            </Link>
          </Card>
        ) : !offer.available ? (
          <Card padding="lg">
            <p className="type-body-md">
              Membership isn’t available on your account right now. Questions? Message your care
              team in Clinic.
            </p>
          </Card>
        ) : (
          <JoinForm offer={offer} onJoined={setWelcome} />
        )}
      </div>
    </div>
  );
}

function MembershipWelcome({ welcome }: { welcome: Welcome }) {
  return (
    <Card padding="lg">
      <Card.Header>
        <Card.Title>Welcome to System Membership</Card.Title>
      </Card.Header>
      <Card.Body>
        <ul className="flex flex-col gap-8 type-body-md">
          {welcome.finishing ? (
            <li>Your payment went through. We’re finishing your membership setup now.</li>
          ) : (
            <>
              <li>{welcome.discountPercent}% off is now on every renewal and Shop order.</li>
              {welcome.perkCents ? (
                <li>
                  {dollars(welcome.perkCents)} off your {welcome.planName ?? 'next'} renewal
                  {welcome.renewalDate ? ` on ${welcome.renewalDate}` : ''}.
                </li>
              ) : null}
              <li>Your first at-home blood test kit ships soon.</li>
            </>
          )}
        </ul>
        <Link to="/app" className="mt-15 inline-block type-body-sm text-accent hover:underline">
          Back to Home →
        </Link>
      </Card.Body>
    </Card>
  );
}

function JoinForm({
  offer,
  onJoined,
}: {
  offer: MembershipOffer;
  onJoined: (welcome: Welcome) => void;
}) {
  const subscriptions = useMySubscriptions();
  const methods = usePaymentMethods();
  const purchase = usePurchaseMembership();
  const plans = useMemo(() => {
    const byId = new Map(
      (subscriptions.data?.subscriptions ?? []).map((s) => [s.stripe_subscription_id, s]),
    );
    return offer.subscriptions.map((plan) => ({ ...plan, sub: byId.get(plan.id) ?? null }));
  }, [offer.subscriptions, subscriptions.data]);
  const [perkPlan, setPerkPlan] = useState<string | null>(null);
  const [pmId, setPmId] = useState<string | null>(null);
  const [avoidPm, setAvoidPm] = useState<string | null>(null);
  // Pre-ticked like the intake's membership agreement; unticking disables Join.
  const [consent, setConsent] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [working, setWorking] = useState(false);
  const requestId = useRef<string | null>(null);
  const busy = useRef(false);
  const props = {
    source: offer.source,
    experiment_key: offer.experiment_key,
    variant_key: offer.variant_key,
  };

  useEffect(() => {
    track('membership_checkout_viewed', { ...props, surface: 'portal', ...repOfferTags(offer.source) });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // The plan with the biggest welcome discount, until the patient picks one.
  useEffect(() => {
    if (perkPlan || !plans.length) return;
    setPerkPlan([...plans].sort((a, b) => b.perk_cents - a.perk_cents)[0].id);
  }, [plans, perkPlan]);

  const selectedCard = methods.data?.payment_methods.find((card) => card.id === pmId) ?? null;
  const publishableKey = methods.data?.publishable_key ?? null;
  const sheet = useMemo<WalletSheet>(
    () => ({
      total: { label: 'System Labs', amount: offer.price_cents },
      displayItems: [{ label: 'System Membership · 1 year', amount: offer.price_cents }],
      applePay: {
        recurringPaymentRequest: {
          paymentDescription: 'System Membership',
          managementURL: `${window.location.origin}/app/account`,
          regularBilling: {
            amount: offer.price_cents,
            label: 'System Membership',
            recurringPaymentIntervalUnit: 'year',
            recurringPaymentIntervalCount: 1,
          },
        },
      },
    }),
    [offer.price_cents],
  );
  const wallet = useApplePaySheet({
    enabled: selectedCard?.wallet === 'apple_pay',
    publishableKey,
    sheet,
    onPaymentMethod: (event) => void join(event),
  });

  async function join(walletEvent?: PaymentRequestPaymentMethodEvent) {
    if (busy.current) return;
    busy.current = true;
    setWorking(true);
    setError(null);
    requestId.current ??= crypto.randomUUID();
    const body: MembershipPurchaseBody = {
      request_id: requestId.current,
      consent: true,
      expected_amount_cents: offer.price_cents,
      subscription_id: plans.length ? perkPlan : null,
      payment_method_id: walletEvent ? null : pmId,
      wallet_payment_method_id: walletEvent?.paymentMethod.id ?? null,
      invite: membershipInvite(),
    };
    // Every retry continues the attempt the server is holding (it may be an
    // earlier one, e.g. after a reload); it never charges twice.
    const send = async () => {
      const result = await purchase.mutateAsync({ ...body, request_id: requestId.current! });
      if (result.request_id) requestId.current = result.request_id;
      return result;
    };
    try {
      let result = await send();
      if (walletEvent) {
        walletEvent.complete('success');
        wallet.done();
      }
      if (result.status === 'requires_action') {
        const stripe = publishableKey ? await getStripe(publishableKey) : null;
        if (!stripe || !result.client_secret)
          throw new Error('Card verification couldn’t load. Tap Join to try again.');
        const verification = await stripe.confirmCardPayment(result.client_secret);
        if (verification.error)
          throw new Error(verification.error.message || 'Your bank needs you to verify this card.');
        result = await send();
      }
      for (let attempt = 0; result.status === 'processing' && attempt < 4; attempt += 1) {
        await sleep(2000);
        result = await send();
      }
      // Joined only once the fee is paid: active, or paid with setup finishing.
      const paid = result.status === 'active' || (result.status === 'processing' && result.paid);
      if (!paid)
        throw new Error(
          result.status === 'requires_action'
            ? 'Your bank still needs you to verify this card. Tap Join to try again.'
            : 'Your payment is still processing. Check back in a few minutes before trying again.',
        );
      track('membership_joined', {
        ...props,
        payment: walletEvent ? 'apple_pay' : 'card',
        perk_cents: result.perk_cents ?? null,
        finishing: result.status !== 'active',
      });
      clearMembershipLinks();
      requestId.current = null;
      const plan = plans.find((p) => p.id === perkPlan);
      onJoined({
        discountPercent: offer.discount_percent,
        perkCents: result.perk_cents ?? null,
        planName: plan?.sub?.product_name ?? null,
        renewalDate: shortDate(plan?.sub?.next_renewal_at),
        finishing: result.status !== 'active',
      });
    } catch (err) {
      if (walletEvent) {
        walletEvent.complete('fail');
        wallet.done();
      }
      const code = err instanceof ApiError ? err.code : 'verification_failed';
      if (code === 'charge_failed') setAvoidPm(pmId);
      // A declined or closed attempt is over; the next tap is a new purchase.
      if (code === 'charge_failed' || code === 'membership_purchase_closed') requestId.current = null;
      setError(err instanceof Error ? err.message : 'Something went wrong. Try again.');
      track('membership_join_failed', { ...props, error_code: code });
    } finally {
      busy.current = false;
      setWorking(false);
    }
  }

  function onJoinClick() {
    if (!consent || working) return;
    track('membership_join_clicked', {
      ...props,
      payment: wallet.available ? 'apple_pay' : 'card',
    });
    // Apple Pay must open straight from the tap.
    if (wallet.available) {
      if (!wallet.show()) setError('Apple Pay is getting ready. Tap again in a moment.');
      return;
    }
    void join();
  }

  const tests = Math.round(12 / offer.lab_interval_months);
  const benefits: [string, string][] = [
    [
      `${offer.perk_percent}% off your next renewal`,
      'A welcome discount on the plan you pick below, worth half of one month.',
    ],
    [
      `${offer.discount_percent}% off everything after`,
      'Every renewal, new treatment and Shop order, for as long as you’re a member.',
    ],
    [
      `${tests} at-home blood tests a year`,
      `A test every ${offer.lab_interval_months} months (a ${dollars(offer.lab_annual_value_cents)} value). Your first kit ships when you join.`,
    ],
    ['Priority support & order processing', 'Plus shipping protection and member-only deals.'],
  ];
  const admin = offer.source === 'admin';

  return (
    <>
      {admin && (
        <div className="border border-accent bg-accent-wash px-15 py-10 type-body-sm">
          Rep session{offer.rep ? ` (${offer.rep})` : ''}: this enrollment is recorded as yours.
          Confirm the patient agrees before joining.
        </div>
      )}
      <Card padding="lg">
        <div className="flex items-baseline justify-between gap-10 flex-wrap">
          <span className="type-label-sm text-ink-muted uppercase">Annual membership</span>
          <span className="type-title-lg">{dollars(offer.price_cents)} / yr</span>
        </div>
        <ul className="mt-15 flex flex-col gap-12">
          {benefits.map(([title, detail]) => (
            <li key={title} className="flex gap-10">
              <span aria-hidden className="text-accent">
                ✓
              </span>
              <span>
                <span className="block type-body-md">{title}</span>
                <span className="block type-body-sm text-ink-muted">{detail}</span>
              </span>
            </li>
          ))}
        </ul>
      </Card>

      {plans.length > 0 && (
        <Card padding="lg">
          <Card.Header>
            <Card.Title>Pick your 50% off plan</Card.Title>
          </Card.Header>
          <Card.Body>
            <div className="flex flex-col gap-8" role="radiogroup">
              {plans.map((plan) => {
                const date = shortDate(plan.sub?.next_renewal_at);
                const checked = perkPlan === plan.id;
                return (
                  <label
                    key={plan.id}
                    className={`flex items-center justify-between gap-10 border px-12 py-10 cursor-pointer ${checked ? 'border-ink' : 'border-line-subtle'}`}
                  >
                    <span className="flex items-center gap-10">
                      <input
                        type="radio"
                        name="perk-plan"
                        checked={checked}
                        onChange={() => {
                          requestId.current = null;
                          setPerkPlan(plan.id);
                          track('membership_perk_selected', { ...props, perk_cents: plan.perk_cents });
                        }}
                      />
                      <span>
                        <span className="block type-body-md">
                          {plan.sub?.product_name ?? 'Your plan'}
                        </span>
                        <span className="block type-body-sm text-ink-muted">
                          {date ? `Renews ${date}` : 'Next renewal'}:{' '}
                          <s>{dollars(plan.renewal_cents)}</s>{' '}
                          {dollars(plan.renewal_cents - plan.perk_cents)}
                        </span>
                      </span>
                    </span>
                    <span className="type-label-sm text-accent whitespace-nowrap">
                      Save {dollars(plan.perk_cents)}
                    </span>
                  </label>
                );
              })}
            </div>
          </Card.Body>
        </Card>
      )}

      <Card padding="lg">
        {/* The picker carries its own PAYMENT label. */}
        <Card.Body>
          <PaymentMethodPicker
            selectedPmId={pmId}
            onSelect={(id) => {
              // A different card is a new attempt; the server cancels the old one.
              if (id !== pmId) requestId.current = null;
              setPmId(id);
            }}
            avoidPmId={avoidPm}
          />
          <label className="flex gap-10 items-start type-body-sm text-ink-secondary">
            <input
              type="checkbox"
              className="mt-3"
              checked={consent}
              onChange={(e) => setConsent(e.target.checked)}
            />
            <span>
              {admin ? 'The patient agrees to ' : 'I agree to '}
              the annual System Membership ({dollars(offer.price_cents)}/year, charged today and
              renewing yearly until cancelled) and consent to at-home collection, laboratory
              testing and sharing results with the care team.
            </span>
          </label>
          {error && <p className="mt-10 type-body-sm text-danger">{error}</p>}
          <Button
            variant="filled"
            className="mt-15"
            loading={working}
            disabled={!consent || (!pmId && !wallet.available) || (plans.length > 0 && !perkPlan)}
            onClick={onJoinClick}
          >
            {wallet.available
              ? `Join with Apple Pay · ${dollars(offer.price_cents)}`
              : `Join for ${dollars(offer.price_cents)}/yr`}
          </Button>
        </Card.Body>
      </Card>
    </>
  );
}
